import { useState, useEffect, useCallback } from 'react';
import { listDocuments, getDocumentAccess, getDocumentStatus, setDocumentAccess, getErrorMessage } from '../../api/documents';
import { useAuth } from '../../context/AuthContext';

const NIVEAUX = [
  { value: 'debutant', label: 'Débutant' },
  { value: 'intermediaire', label: 'Intermédiaire' },
  { value: 'avance', label: 'Avancé' },
];

// Matrice DOCUMENT × niveau : coche = ce niveau peut consulter CE document précis
// (par son titre), plus par catégorie entière.
export default function DocumentAccessPanel() {
  const { token } = useAuth();

  const [documents, setDocuments] = useState([]);
  const [matrix, setMatrix] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(null); // id du document en cours de sauvegarde
  const [connected, setConnected] = useState(false);

  const load = useCallback((background = false) => {
    if (!background) setLoading(true);
    Promise.all([listDocuments(token), getDocumentAccess(token), getDocumentStatus(token)])
      .then(([docs, access]) => {
        setDocuments(docs);
        setMatrix(access);
        setConnected(true);
        setError('');
      })
      .catch((err) => {
        setConnected(false);
        setError(getErrorMessage(err, "Backend déconnecté — modifications d'accès désactivées."));
      })
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(() => {
    load();
    const refresh = setInterval(() => load(true), 5000);
    return () => clearInterval(refresh);
  }, [load]);

  const toggle = async (documentId, niveauValue) => {
    if (!connected || saving !== null) return;
    const key = String(documentId);
    const current = matrix[key] || [];
    const next = current.includes(niveauValue)
      ? current.filter((n) => n !== niveauValue)
      : [...current, niveauValue];

    setMatrix((prev) => ({ ...prev, [key]: next }));
    setSaving(documentId);
    try {
      await setDocumentAccess(token, documentId, next);
    } catch (err) {
      setError(getErrorMessage(err, "Impossible d'enregistrer ce changement."));
      load(); // revert en rechargeant l'état serveur
    } finally {
      setSaving(null);
    }
  };

  if (loading) return <p className="admin-empty">Chargement…</p>;

  return (
    <div className="admin-card admin-card-wide">
      <h2>Accès aux documents par niveau</h2>
      <div className={connected ? 'document-status connected' : 'document-status disconnected'}>
        {connected ? 'BACKEND CONNECTÉ — SOURCE : SERVEUR' : 'BACKEND DÉCONNECTÉ — OPÉRATIONS DÉSACTIVÉES'}
      </div>
      <p className="admin-hint">
        Cochez les niveaux autorisés à consulter chaque document. L'accès se donne
        document par document, pas par type entier.
      </p>
      {error && <div className="auth-error">{error}</div>}

      {documents.length === 0 ? (
        <p className="admin-empty">Aucun document importé pour le moment.</p>
      ) : (
        <div className="documents-table-wrap">
        <table className="admin-table access-matrix documents-table">
          <thead>
            <tr>
              <th>Document</th>
              {NIVEAUX.map((n) => <th key={n.value}>{n.label}</th>)}
            </tr>
          </thead>
          <tbody>
            {documents.map((d) => (
              <tr key={d.id} className={saving === d.id ? 'row-saving' : ''}>
                <td>
                  <div className="access-doc-title">{d.title}</div>
                  <div className="access-doc-type">{d.doc_type_label}</div>
                </td>
                {NIVEAUX.map((n) => (
                  <td key={n.value} className="access-cell">
                    <input
                      type="checkbox"
                      checked={(matrix[String(d.id)] || []).includes(n.value)}
                      onChange={() => toggle(d.id, n.value)}
                      disabled={!connected || saving !== null}
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      )}
    </div>
  );
}
